import { LoggingService } from "./loggingSvc";
import { UiControlConstants } from "../../ShellCommon/common/uicontrol-constants";

/**
 * The service responsible for showing modal dialogs.
 */
export interface DialogService {
  /** Shows arbitrary content in the application dialog, allowing data to be passed in, which will be hosted on the scope. */
  /** @param content The content to compile and render inside the dialog. */
  /** @param title The title that should be shown above the rendered content. */
  /** @param data Arbitrary object that will be hosted on the scope of the content rendered in the dialog (merged into scope). */
  /** @param hideCloseButton True if the close button should be hidden when showing the dialog, false otherwise. */
  showContent(content: string, title: string, data: any, hideCloseButton: boolean): void;

  /** Hides any visible dialog. */
  hide(): void;
}

export class DialogServiceImplementation implements DialogService {
  public lastHideCloseButtonValue: boolean;
  private lastRegisteredTitle: string;
  private dialogController: any;
  private lastScope: ng.IScope;
  private dialogWasShown: boolean;

  // @ngInject
  constructor(
    private $compile: ng.ICompileService,
    private $rootScope: ng.IRootScopeService,
    private loggingService: LoggingService,
    private uiControlConstants: UiControlConstants
  ) {
    $rootScope.$on("$stateChangeStart", () => {
      if (this.dialogWasShown) {
        this.hide();
      }
    });
  }

  showContent(content: string, title: string, data: any, hideCloseButton: boolean) {
    if (this.lastScope) {
      this.loggingService.logError("Tried to show modal content when content was already being displayed!");
      return;
    }
    if (!this.dialogController) {
      this.loggingService.logError("Tried to show modal, but the modal does not exist yet.");
      return;
    }

    this.lastScope = this.$rootScope.$new(true);
    angular.extend(this.lastScope, data);
    let result = angular.element("#dialogServiceContentTarget").html(content);
    this.lastRegisteredTitle = title;
    this.lastHideCloseButtonValue = hideCloseButton;
    if (!this.dialogWasShown) {
      this.wrapFocusToDialog(result);
    }
    this.$compile(result)(this.lastScope);
    this.dialogWasShown = true;
    this.dialogController.show();
  }

  private wrapFocusToDialog(contentElement: JQuery) {
    let tabKey = this.uiControlConstants.keyCode.tabKeyCode;
    let parentsUntilDialog = contentElement.parentsUntil("[role='dialog']");
    let dialogParentElement = parentsUntilDialog[parentsUntilDialog.length - 1].parentElement;
    let lastElement = angular.element("#dialogServiceLastElement")[0];
    lastElement.addEventListener(
      "keydown",
      (event) => {
        if (event.keyCode == tabKey && !event.shiftKey) {
          event.preventDefault();
          dialogParentElement.focus();
        }
      },
      false
    );
    dialogParentElement.addEventListener(
      "keydown",
      (event) => {
        if (event.keyCode == tabKey && event.shiftKey && event.srcElement == dialogParentElement) {
          event.preventDefault();
          lastElement.focus();
        }
      },
      false
    );
  }

  hide() {
    if (this.dialogController) {
      this.dialogController.hide();
    }

    angular.element("#dialogServiceContentTarget").empty();
    if (this.lastScope) {
      this.lastScope.$destroy();
      this.lastScope = undefined;
    }
  }

  get currentTitle(): string {
    return this.lastRegisteredTitle;
  }

  get hideCloseButton(): boolean {
    return this.lastHideCloseButtonValue;
  }

  registerDialog(dialogController: { show: () => void; hide: () => void }): void {
    this.dialogController = dialogController;
  }
}
